import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ImpersonationAPI, UserAPI, TenantSettingsAPI } from '../services/api';
import { clearTenantSessionStorage, setTenantUuid, setUserId } from '../utils/storage';
import { SESSION_REJECTED_EVENT } from '../services/sessionRecovery';

const TenantContext = createContext();
const INTERNAL_ADMIN_ROLES = new Set(['superadmin', 'ops', 'support', 'finance']);
const ADMIN_AUTH_TOKEN_KEY = 'admin_auth_token';
const ADMIN_ROLE_KEY = 'admin_auth_role';
const ADMIN_SESSION_EXP_KEY = 'admin_auth_session_expires_at';

// Dev/test tenant list for local switching (disabled by default)
const ENABLE_TEST_TENANTS = process.env.REACT_APP_ENABLE_TEST_TENANTS === 'true';
const TEST_TENANTS = ENABLE_TEST_TENANTS ? [
  { id: 1, uuid: '00000000-0000-0000-0000-000000000001', name: 'Aiker (Default)', description: 'Legacy demo tenant' },
  { id: 5, uuid: '8de2c0c1-2ed7-4c3b-aaac-d7406a4ab25e', name: 'Dev / Test Tenant', description: 'Shared sandbox tenant' },
] : [];

const DEFAULT_TENANT_ID = TEST_TENANTS.length ? TEST_TENANTS[0].id : null;

const formatFallbackTenantName = (uuid) => {
  if (!uuid) return 'Unknown Tenant';
  return `Tenant ${uuid.slice(0, 8)}`;
};

const persistTenantMetadata = (uuid, name) => {
  if (!uuid) return null;
  const resolvedName = name || localStorage.getItem('tenant_name') || formatFallbackTenantName(uuid);
  setTenantUuid(uuid);
  localStorage.setItem('tenant_name', resolvedName);
  return { uuid, name: resolvedName };
};

const persistAdminBridgeSession = (data) => {
  if (!INTERNAL_ADMIN_ROLES.has((data?.role || '').trim().toLowerCase()) || !data?.token) {
    localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
    localStorage.removeItem(ADMIN_ROLE_KEY);
    localStorage.removeItem(ADMIN_SESSION_EXP_KEY);
    return;
  }

  localStorage.setItem(ADMIN_AUTH_TOKEN_KEY, data.token);
  localStorage.setItem(ADMIN_ROLE_KEY, data.role);
  const sessionExpiry = data.session_expires_at || data.expires_at;
  if (sessionExpiry) {
    localStorage.setItem(ADMIN_SESSION_EXP_KEY, sessionExpiry);
  } else {
    localStorage.removeItem(ADMIN_SESSION_EXP_KEY);
  }
};

export const TenantProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('authToken'));
  const [currentTenant, _setCurrentTenant] = useState(() => {
    const saved = localStorage.getItem('aiker_current_tenant');
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (TEST_TENANTS.some((t) => t.id === parsed)) return parsed;
      localStorage.removeItem('aiker_current_tenant');
    }
    return DEFAULT_TENANT_ID;
  });

  const [tenantInfo, setTenantInfo] = useState(() => {
    const uuid = localStorage.getItem('tenant_uuid');
    const name = localStorage.getItem('tenant_name');
    return uuid ? { uuid, name: name || formatFallbackTenantName(uuid) } : null;
  });

  const [tenantTimezone, setTenantTimezone] = useState('Asia/Shanghai');
  const [userRole, setUserRole] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [tenantStatus, setTenantStatus] = useState('active');
  const [isTenantSessionLoading, setIsTenantSessionLoading] = useState(() => !!localStorage.getItem('authToken'));

  useEffect(() => {
    const onSessionRejected = () => {
      setIsAuthenticated(false);
      setTenantInfo(null);
      setUserRole(null);
      setEntitlements(null);
      setTenantStatus('active');
      setTenantTimezone('Asia/Shanghai');
      setIsTenantSessionLoading(false);
    };
    window.addEventListener(SESSION_REJECTED_EVENT, onSessionRejected);
    return () => window.removeEventListener(SESSION_REJECTED_EVENT, onSessionRejected);
  }, []);

  // Load tenant settings (including timezone) when we have a tenant UUID
  useEffect(() => {
    if (!tenantInfo?.uuid) return;
    const token = localStorage.getItem('authToken');
    TenantSettingsAPI.get(tenantInfo.uuid)
      .then((info) => {
        if (localStorage.getItem('authToken') !== token) return;
        if (info?.timezone) setTenantTimezone(info.timezone);
      })
      .catch(() => {});
  }, [tenantInfo?.uuid]);

  useEffect(() => {
    if (currentTenant == null) {
      localStorage.removeItem('aiker_current_tenant');
    } else {
      localStorage.setItem('aiker_current_tenant', currentTenant);
    }
  }, [currentTenant]);

  const refreshTenantName = useCallback(async (uuid) => {
    if (!uuid) return;
    const token = localStorage.getItem('authToken');
    try {
      const info = await TenantSettingsAPI.get(uuid);
      if (!token || localStorage.getItem('authToken') !== token) return;
      const displayName = info?.company_name || info?.companyName || info?.name;
      if (displayName) {
        const meta = persistTenantMetadata(uuid, displayName);
        setTenantInfo(meta);
      }
      if (info?.timezone) setTenantTimezone(info.timezone);
    } catch (error) {
      console.warn('[TenantContext] Failed to fetch tenant display name:', error);
    }
  }, []);

  // Auto-fetch tenant_uuid if user is authenticated but tenant context missing
  useEffect(() => {
    const ensureTenantContext = async () => {
      if (!isAuthenticated) {
        setIsTenantSessionLoading(false);
        return;
      }
      if (entitlements !== null && localStorage.getItem('tenant_uuid')) {
        setIsTenantSessionLoading(false);
        return;
      }
      setIsTenantSessionLoading(true);
      let storedUUID = localStorage.getItem('tenant_uuid');
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        try {
          const userInfo = await UserAPI.getCurrentUser();
          if (localStorage.getItem('authToken') !== authToken) return;
          if (userInfo?.user_id || userInfo?.id) {
            setUserId(userInfo.user_id || userInfo.id);
          }
          setUserRole(userInfo?.role || null);
          setEntitlements(userInfo?.entitlements || {});
          if (userInfo?.tenant_status) setTenantStatus(userInfo.tenant_status);
          if (userInfo?.tenant_uuid && userInfo.tenant_uuid !== storedUUID) {
            localStorage.removeItem('tenant_name');
            storedUUID = userInfo.tenant_uuid;
            const meta = persistTenantMetadata(storedUUID);
            setTenantInfo(meta);
            console.log('[TenantContext] Synced tenant_uuid from current user:', storedUUID);
          } else if (!storedUUID) {
            console.warn('[TenantContext] User has no tenant_uuid, using fallback');
          }
        } catch (error) {
          if (localStorage.getItem('authToken') !== authToken) return;
          console.warn('[TenantContext] Failed to fetch tenant_uuid:', error);
          setEntitlements({});
          setUserRole(null);
        }
      }
      if (storedUUID) {
        const storedName = localStorage.getItem('tenant_name');
        setTenantInfo({ uuid: storedUUID, name: storedName || formatFallbackTenantName(storedUUID) });
        refreshTenantName(storedUUID);
      }
      setIsTenantSessionLoading(false);
    };

    ensureTenantContext();
  }, [isAuthenticated, entitlements, refreshTenantName]);

  // Refresh display name when we have UUID but no friendly label
  useEffect(() => {
    if (tenantInfo?.uuid && (!tenantInfo.name || tenantInfo.name.startsWith('Tenant '))) {
      refreshTenantName(tenantInfo.uuid);
    }
  }, [tenantInfo?.uuid, tenantInfo?.name, refreshTenantName]);

  // Impersonation support: honor ?impersonate=token and auto-login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('impersonate');
    if (!token) return;

    // Clear URL parameter immediately to prevent replay on refresh
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);

    (async () => {
      try {
        const res = await ImpersonationAPI.validate(token);
        if (res?.user_id) {
          // Save impersonation target info before clearing admin session
          localStorage.setItem('authToken', token);
          localStorage.setItem('userRole', 'admin');
          setUserRole('admin');
          setUserId(res.user_id);
          // Also set tenant_uuid from the response if available, or fetch it
          if (res.tenant_id) {
            localStorage.removeItem('tenant_name');
            const meta = persistTenantMetadata(res.tenant_id);
            setTenantInfo(meta);
            refreshTenantName(res.tenant_id);
          }
          setIsAuthenticated(true);
          // Redirect straight into the app once impersonation succeeds
          window.location.replace('/app/extensions');
        }
      } catch (e) {
        console.warn('[TenantContext] Invalid impersonation token', e);
      }
    })();
  }, [refreshTenantName]);  // Run once per stable tenant-name refresher, regardless of isAuthenticated state

  const getCurrentTenantInfo = () => {
    if (tenantInfo) {
      return tenantInfo;
    }
    return { uuid: null, name: 'Unknown Tenant' };
  };

  const setCurrentTenant = (tenantId) => {
    if (!ENABLE_TEST_TENANTS) return;
    if (TEST_TENANTS.some((t) => t.id === tenantId)) {
      _setCurrentTenant(tenantId);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/users/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        clearTenantSessionStorage();
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userRole', data.role);
        setUserRole(data.role || null);
        setUserId(data.user_id);
        setEntitlements(null);
        setTenantStatus('active');
        setTenantInfo(null);
        persistAdminBridgeSession(data);
        if (data.tenant_uuid) {
          const meta = persistTenantMetadata(data.tenant_uuid);
          setTenantInfo(meta);
          refreshTenantName(data.tenant_uuid);
        }
        setIsAuthenticated(true);
        return { success: true, ...data };
      }
      let message = 'Login failed';
      let code = null;
      if (typeof data.detail === 'string') {
        message = data.detail;
      } else if (data.detail && typeof data.detail === 'object') {
        message = data.detail.message || message;
        code = data.detail.error_code || null;
      }
      const err = new Error(message);
      if (code) err.code = code;
      throw err;
    } catch (error) {
      console.error('Login API error:', error);
      throw error;
    }
  };

  const logout = async () => {
    const bearer = localStorage.getItem('authToken');
    if (bearer) {
      try {
        const endpoint = INTERNAL_ADMIN_ROLES.has(userRole) ? '/api/users/logout' : '/api/enduser/logout';
        const response = await fetch(endpoint, { method: 'POST', headers: { Authorization: `Bearer ${bearer}` } });
        if (!response.ok && ![401, 403].includes(response.status)) throw new Error('logout_failed');
      } catch (_error) {
        window.alert('退出未完成，请检查网络后重试。 / Sign out failed. Please retry.');
        return;
      }
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    clearTenantSessionStorage();
    localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
    localStorage.removeItem(ADMIN_ROLE_KEY);
    localStorage.removeItem(ADMIN_SESSION_EXP_KEY);
    setTenantInfo(null);
    setEntitlements(null);
    setTenantStatus('active');
    setUserRole(null);
    setIsTenantSessionLoading(false);
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  const value = {
    currentTenant,
    setCurrentTenant,
    getCurrentTenantInfo,
    tenantInfo,
    tenantName: tenantInfo?.name,
    tenantTimezone,
    userRole,
    tenants: TEST_TENANTS,
    testTenants: TEST_TENANTS,
    isAuthenticated,
    login,
    logout,
    entitlements,
    tenantStatus,
    isTenantSessionLoading,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};
